/**
 * Client-side head management.
 *
 * On a prerendered page the tags are already correct on first paint, so this hook
 * is a no-op for the initial route. It exists for soft navigation, where React
 * Router changes the URL without a document load and the head would otherwise
 * keep the previous page's title, description and structured data.
 */

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { SITE } from '@/config/site'

const setMeta = (selector: string, attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

const setLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    document.head.appendChild(el)
  }
  el.href = href
}

export const useSeo = (): void => {
  const { pathname } = useLocation()

  useEffect(() => {
    let cancelled = false
    /*
     * Dynamic, not static. A static import pulls the entire landing-page corpus
     * into every route's initial chunk — including /resume-builder, which never
     * renders a landing page. Prerendered HTML already carries the correct tags,
     * so this only has to run for soft navigation, where a microtask of delay is
     * invisible.
     */
    void import('./meta').then(({ resolveMeta }) => {
      if (cancelled) return
      const meta = resolveMeta(pathname)

      document.title = meta.title
      setMeta('meta[name="description"]', 'name', 'description', meta.description)
      // Must match scripts/prerender.ts, or a soft navigation would silently
      // narrow what search engines may extract from the page.
      setMeta(
        'meta[name="robots"]',
        'name',
        'robots',
        meta.noindex
          ? 'noindex,follow'
          : 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1',
      )
      setLink('canonical', meta.canonical)

      setMeta('meta[property="og:title"]', 'property', 'og:title', meta.title)
      setMeta('meta[property="og:description"]', 'property', 'og:description', meta.description)
      setMeta('meta[property="og:url"]', 'property', 'og:url', meta.canonical)
      setMeta('meta[property="og:type"]', 'property', 'og:type', meta.ogType ?? 'website')
      if (meta.ogImage) {
        setMeta('meta[property="og:image"]', 'property', 'og:image', meta.ogImage)
        setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', meta.ogImage)
      }
      setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', SITE.name)
      setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image')
      setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title)
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', meta.description)

      // Replace rather than append: leaving a previous page's JSON-LD in the head
      // during a soft navigation would describe the wrong page to any crawler that
      // executes JavaScript.
      for (const node of document.head.querySelectorAll('script[data-seo-jsonld]')) node.remove()
      for (const block of meta.jsonLd ?? []) {
        const script = document.createElement('script')
        script.type = 'application/ld+json'
        script.dataset.seoJsonld = 'true'
        script.textContent = JSON.stringify(block)
        document.head.appendChild(script)
      }
    })

    return () => {
      cancelled = true
    }
  }, [pathname])
}
